import React from 'react';
import { HiOutlineCheck } from 'react-icons/hi2';
import { FaCloud, FaPlus } from 'react-icons/fa';

// UI copy keyed by plan name. Mirrors the extension's UpsellModalD. New plans
// from the API render with empty defaults until copy is added here.
const PLAN_COPY = {
  pro_2026: { tagline: 'For regular use', badge: null },
  proplus_2026: { tagline: 'For power users', badge: 'Most Popular' },
  team_2026: { tagline: 'Share PRO with your team', badge: null },
};

const EMPTY_COPY = { tagline: '', badge: null };

// Format a raw credit amount as a compact label (10000 -> "10,000").
const formatCredits = (amount) => (amount ?? 0).toLocaleString();

const PRO_BASE_FEATURES = [
  {
    text: 'Unlocks all PRO features',
    tooltip:
      'Unlocks the full browser extension on your own computer — every PRO feature enabled, no limits, no cloud credits consumed.\n\nRuns locally, so your data stays with you.',
  },
  { text: 'Unlimited exports (CSV, Excel, JSON)' },
];

const CLOUD_ACCESS_FEATURE = {
  text: 'Cloud Access',
  caption: '(Uses credits, pay as you go)',
  cloud: true,
  tooltip:
    'Run extractions on our cloud platform — no browser needed.\n\nCloud usage is credit-based, not unlimited: 1 cloud credit = 1 page extracted in the cloud, and AI features like Page Unblocker and Data Labeling also consume credits. Your plan includes a yearly credit allowance, and you can top up anytime.\n\nRunning the extension locally on your own browser is always unlimited.',
};

// Build the per-plan feature list. The credit bonus line is derived from the
// plan's real credit amount so it stays in sync with the backend.
const buildFeatures = (plan) => {
  const creditLabel = formatCredits(plan.credits?.amount);
  const bonus = {
    text: `Bonus: ${creditLabel} cloud credits / year`,
    highlight: plan.name === 'proplus_2026',
    tooltip: `${creditLabel} cloud credits included each year to run extractions on our cloud platform.\n\nCredits renew yearly, so you start each year with a full balance.`,
  };

  if (plan.features?.team_collab) {
    const seats = plan.features?.default_seats;
    return [
      { text: 'Everything included in PRO', icon: 'plus' },
      CLOUD_ACCESS_FEATURE,
      { ...bonus, highlight: false },
      {
        text: `Share PRO with team: ${seats} seats`,
        highlight: true,
        tooltip: `Invite teammates to a shared workspace. Every member unlocks the full PRO browser extension, and your team works from one shared dashboard with the same automations, schedules, and cloud database.\n\nIncluded seats: ${seats}. Seats can be reassigned anytime.`,
      },
    ];
  }

  return [...PRO_BASE_FEATURES, CLOUD_ACCESS_FEATURE, bonus];
};

const FeatureTooltip = ({ tooltip, side = 'right' }) => {
  const isLeft = side === 'left';
  return (
    <span className={`pointer-events-none absolute top-1/2 -translate-y-1/2 w-56 opacity-0 scale-95 group-hover/tip:opacity-100 group-hover/tip:scale-100 transition-all duration-200 z-50 ${isLeft ? 'right-full mr-3' : 'left-full ml-3'}`}>
      <span className="block px-3.5 py-2.5 text-xs font-normal text-gray-100 bg-gray-900/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-xl shadow-cyan-500/10 dark:shadow-cyan-500/5 border border-cyan-500/20 dark:border-cyan-400/15 leading-relaxed whitespace-pre-line text-left">
        {tooltip}
      </span>
      <span className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-gray-900/95 dark:bg-gray-800/95 rotate-45 ${isLeft ? '-right-1.5 border-t border-r border-cyan-500/20 dark:border-cyan-400/15' : '-left-1.5 border-b border-l border-cyan-500/20 dark:border-cyan-400/15'}`} />
    </span>
  );
};

const PlanCardV2 = ({ plan, isFeatured, isLast, getPlanAction, actionLoading }) => {
  const config = PLAN_COPY[plan.name] || EMPTY_COPY;
  const features = buildFeatures(plan);
  const { label, disabled, action, tooltip: ctaTooltip } = getPlanAction(plan);
  const tooltipSide = isLast ? 'left' : 'right';

  return (
    <div className={`relative flex flex-col h-full rounded-2xl px-6 pt-9 pb-8 transition-all duration-500 hover:z-30 ${
      isFeatured
        ? 'border-2 border-cyan-400/50 dark:border-cyan-400/40 bg-gradient-to-br from-cyan-400/[0.08] via-blue-500/[0.04] to-purple-500/[0.04] dark:from-cyan-400/[0.06] dark:via-blue-500/[0.03] dark:to-gray-900/80 shadow-glow-cyan md:scale-[1.03] z-10 hover:shadow-glow-md'
        : 'border border-gray-200 dark:border-gray-700/30 bg-gradient-to-br from-gray-50 dark:from-gray-900/60 to-gray-100 dark:to-gray-800/40 hover:border-gray-300 dark:hover:border-gray-600/50 hover:shadow-md'
    }`}>
      {isFeatured && (
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 opacity-[0.06] blur-xl pointer-events-none" />
      )}

      {config.badge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
          <span className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full shadow-lg ${
            isFeatured
              ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white shadow-glow-cyan'
              : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white border border-gray-500/50'
          }`}>
            {config.badge}
          </span>
        </div>
      )}

      {/* Header */}
      <div className={`relative ${config.badge ? 'mt-1' : ''}`}>
        <h4 className={`text-sm font-semibold uppercase tracking-widest ${
          isFeatured
            ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent'
            : 'text-gray-800 dark:text-gray-100'
        }`}>{plan.display_name || plan.name}</h4>
        {config.tagline && <p className="text-[13px] font-medium text-gray-500 dark:text-gray-400 mt-1">{config.tagline}</p>}
      </div>

      {/* Pricing — yearly only */}
      <div className="relative mt-5 mb-6">
        <div className="flex items-baseline gap-2">
          <span className={`text-5xl font-extrabold tracking-tight ${
            isFeatured
              ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent'
              : 'text-gray-900 dark:text-gray-100'
          }`}>
            ${plan.yearly_price}
          </span>
          <span className="text-sm text-gray-400 dark:text-gray-500 font-normal">/yr</span>
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">Billed annually · Cancel anytime</p>
      </div>

      {/* CTA */}
      <div className="relative mb-7">
        {disabled ? (
          <div
            title={ctaTooltip || undefined}
            className={`relative z-10 w-full py-3 rounded-xl font-semibold text-sm text-center transition-all ${
              isFeatured
                ? 'text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 border border-cyan-400/20 dark:border-cyan-400/15'
                : 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/30'
            } ${ctaTooltip ? 'cursor-help' : 'cursor-default'}`}
          >
            {label}
          </div>
        ) : (
          <button
            onClick={action}
            disabled={actionLoading === plan.name}
            className={`relative z-10 w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-300 transform disabled:opacity-50 group cursor-pointer ${
              isFeatured
                ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 text-white shadow-lg shadow-cyan-500/20 hover:shadow-glow-md hover:scale-[1.03] border-0'
                : 'bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 border-0 hover:scale-[1.03] hover:shadow-md'
            }`}
          >
            {actionLoading === plan.name ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Processing...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                {label}
                <span className="group-hover:translate-x-1 transition-transform">&rarr;</span>
              </span>
            )}
          </button>
        )}
      </div>

      {/* Divider */}
      <div className={`mb-6 ${
        isFeatured
          ? 'h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent'
          : 'border-t border-gray-200/40 dark:border-gray-700/30'
      }`} />

      {/* Features */}
      <ul className="relative flex-grow space-y-3">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2.5">
            {feature.icon === 'plus' ? (
              <FaPlus className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-cyan-500 dark:text-cyan-400" />
            ) : feature.cloud ? (
              <FaCloud className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-cyan-500 dark:text-cyan-400" />
            ) : (
              <HiOutlineCheck className={`w-4 h-4 mt-0.5 flex-shrink-0 ${feature.highlight ? 'text-cyan-500 dark:text-cyan-400' : 'text-emerald-500'}`} />
            )}
            <div className="text-sm leading-tight">
              <span className={`relative group/tip flex items-center gap-1.5 ${feature.tooltip ? `cursor-default border-b border-dashed ${feature.highlight ? 'border-amber-400/40' : 'border-gray-400/40'}` : ''} ${
                feature.highlight
                  ? 'font-bold'
                  : feature.cloud
                    ? 'font-semibold bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent'
                    : 'text-gray-600 dark:text-gray-300'
              }`}>
                {feature.highlight ? (
                  <span
                    className="relative inline-block animate-shiny-holo"
                    style={{
                      backgroundImage:
                        'linear-gradient(110deg, #fde68a 0%, #fca5a5 15%, #f0abfc 30%, #a5b4fc 45%, #67e8f9 60%, #fde68a 75%, #fca5a5 90%, #f0abfc 100%)',
                      backgroundSize: '200% 100%',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                      filter: 'drop-shadow(0 0 6px rgba(253, 224, 71, 0.35))',
                    }}
                  >
                    {feature.text}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 overflow-hidden"
                      style={{ WebkitBackgroundClip: 'text', backgroundClip: 'text' }}
                    >
                      <span
                        className="absolute top-0 bottom-0 w-1/3 animate-shiny-sheen"
                        style={{
                          background:
                            'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.85) 50%, transparent 100%)',
                          mixBlendMode: 'overlay',
                        }}
                      />
                    </span>
                  </span>
                ) : (
                  feature.text
                )}
                {feature.tooltip && <FeatureTooltip tooltip={feature.tooltip} side={tooltipSide} />}
              </span>
              {feature.caption && (
                <span className="block text-xs text-gray-400 dark:text-gray-500 font-normal mt-0.5">{feature.caption}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PlanCardV2;
